/* eslint-disable react-hooks/exhaustive-deps */
import { useMediaQuery, Grid, Box, useTheme, Typography, Button, Container, Skeleton } from "@mui/material";
import { useNavigate } from "react-router-dom";
import CustomRightImage from "@components/CustomRightImagev2";
import { useLazyGetTaxSystemConfigQuery } from "@api/applications";
import { useEffect, useState } from "react";
import { ApiError, TaxSystemConfigResponse } from "@interfaces/index";
import { amountToText, calculateAmountMonthly, formatCurrency } from "@helpers/funciones";
import icon_check from "@assets/img/icon_check_cotizador.svg";
import { StyledSlider } from "@components/CustomSlider";

import useInterfaceConfig from "@hooks/useInterfaceConfig";
import { SystemUpdateAlt as SystemUpdateAltIcon } from "@mui/icons-material";
import { usePostDocumentQuotationTableMutation } from "@api/documents";
import { setModalErrorVisible } from "@store/slices/appSlice";
import { useAppDispatch } from "@store/store";
import { ButtonTypeSelectedCustom } from "@components/ButtonTypeSelectedCustom";
import { MarketStructure } from "@interfaces/LeasingTypes";

const CotizadorVivemoto = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobileOrTablet = useMediaQuery(theme.breakpoints.down("md"));
  const interfaceConfig = useInterfaceConfig();
  const dispatch = useAppDispatch();

  const [getTaxSystemConfig] = useLazyGetTaxSystemConfigQuery();
  const [postGenerateQuotationTable] = usePostDocumentQuotationTableMutation();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [configCreditoSimple, setConfigCreditoSimple] = useState<TaxSystemConfigResponse | null>(null);
  const [configArrendamiento, setConfigArrendamiento] = useState<TaxSystemConfigResponse | null>(null);
  const [creditType, setCreditType] = useState<string>("credito_simple");

  const [amountMarks, setAmountMarks] = useState<{ value: number; label: string }[]>([]);
  const [termMarks, setTermMarks] = useState<{ value: number; label: string }[]>([]);

  const [cotizadorConfig, setCotizadorConfig] = useState<TaxSystemConfigResponse>({
    id: "",
    code: "",
    product_id: "",
    minAmount: 0,
    maxAmount: 0,
    stepAmount: 0,
    minTerm: 0,
    maxTerm: 0,
    stepTerm: 0,
    defaultAmount: 0,
    defaultTerm: 0,
    tax_system_type: "",
    defaultAdvance: 0,
    maxAdvance: 0,
    minAdvance: 0,
    stepAdvance: 0,
  });

  const [selectedAmount, setSelectedAmount] = useState<number>(cotizadorConfig.defaultAmount);
  const [selectedTerm, setSelectedTerm] = useState<number>(cotizadorConfig.defaultTerm);
  const [monthlyRate, setMonthlyRate] = useState<number>(0);

  const [advancePercentageMarket, setAdvancePercentageMarket] = useState<MarketStructure[]>([]);
  const [anticipo, setAnticipo] = useState<number>(20);
  const [residualPercentageMarket, setResidualPercentageMarket] = useState<MarketStructure[]>([]);
  const [residual, setResidual] = useState<number>(10);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        const [responseCreditoSimple, responseArrendamiento] = await Promise.all([
          getTaxSystemConfig("CVMPF").unwrap(),
          getTaxSystemConfig("CVAPF").unwrap(),
        ]);
        setConfigCreditoSimple(responseCreditoSimple);
        setConfigArrendamiento(responseArrendamiento);
      } catch (error) {
        console.error("Error fetching tax system configs", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    if (creditType === "credito_simple" && configCreditoSimple) {
      setCotizadorConfig(configCreditoSimple);
    } else if (creditType === "arrendamiento" && configArrendamiento) {
      setCotizadorConfig(configArrendamiento);
    }
  }, [creditType, configCreditoSimple, configArrendamiento]);

  useEffect(() => {
    const { minTerm, maxTerm, stepTerm, defaultAdvance, defaultTerm, defaultAmount, minAmount, maxAmount, stepAmount } =
      cotizadorConfig;
    if (minTerm < maxTerm && stepTerm > 0) {
      const marks = [];
      for (let i = minTerm; i <= maxTerm; i += stepTerm) {
        marks.push({ value: i, label: `${i}` });
      }

      const maxAdvance = cotizadorConfig.maxAdvance ?? 0;
      const minAdvance = cotizadorConfig.minAdvance ?? 0;
      const stepAdvance = cotizadorConfig.stepAdvance ?? 0;

      const listAdvance: MarketStructure[] = [];

      if (stepAdvance > 0 && minAdvance > 0 && maxAdvance >= minAdvance) {
        for (let r = minAdvance; r <= maxAdvance; r += stepAdvance) {
          listAdvance.push({ value: r, label: `${r}%` });
        }
        if (listAdvance.length && listAdvance[listAdvance.length - 1].value !== maxAdvance) {
          listAdvance.push({ value: maxAdvance, label: `${maxAdvance}%` });
        }
      }


      const maxResidual = cotizadorConfig.maxResidual ?? 0;
      const minResidual = cotizadorConfig.minResidual ?? 0;
      const stepResidual = cotizadorConfig.stepResidual ?? 0;

      const listResidual: MarketStructure[] = [];

      if (stepResidual > 0 && minResidual > 0 && maxResidual >= minResidual) {
        for (let r = minResidual; r <= maxResidual; r += stepResidual) {
          listResidual.push({ value: r, label: `${r}%` });
        }
        if (listResidual.length && listResidual[listResidual.length - 1].value !== maxResidual) {
          listResidual.push({ value: maxResidual, label: `${maxResidual}%` });
        }
      }
      if (minAmount < maxAmount && stepAmount > 0) {
        const marks = [];

        marks.push({ value: minAmount, label: amountToText(minAmount) });
        marks.push({ value: maxAmount, label: amountToText(maxAmount) });

        setAmountMarks(marks);
      }

      setAdvancePercentageMarket(listAdvance);
      setSelectedTerm(defaultTerm);
      setTermMarks(marks);
      setSelectedAmount(defaultAmount);
      setAnticipo(defaultAdvance || 30);
      setResidual(cotizadorConfig.defaultResidual || 10);
      setResidualPercentageMarket(listResidual);
    }
  }, [cotizadorConfig]);

  useEffect(() => {
    if (selectedAmount && selectedTerm) {
      let amountToCalculate = selectedAmount;

      // Restar anticipo para ambos productos; para arrendamiento también restar residual
      const advanceRate = anticipo ? anticipo / 100 : 0;
      const residualRate = residual ? residual / 100 : 0;

      if (creditType === "arrendamiento") {
        const financed = selectedAmount * (1 - advanceRate - residualRate);
        amountToCalculate = financed > 0 ? financed : 0;
      } else {
        const financed = selectedAmount * (1 - advanceRate);
        amountToCalculate = financed > 0 ? financed : 0;
      }

      const monthly = calculateAmountMonthly(
        amountToCalculate,
        selectedTerm,
        interfaceConfig?.interest_rate,
        interfaceConfig?.comision,
        interfaceConfig?.iva,
      );
      setMonthlyRate(monthly);
    }
  }, [selectedAmount, selectedTerm, interfaceConfig?.interest_rate, interfaceConfig?.comision, interfaceConfig?.iva, creditType, anticipo, residual]);

  const setConditions = () => {
    if (!cotizadorConfig.product_id || !selectedAmount || !selectedAmount || !monthlyRate) {
      return;
    }
    const sourceCode = sessionStorage.getItem("source_code");

    const urlType = creditType === "credito_simple" ? "creditosimple" : "arrendamiento";

    let urlPortal = `/producto/${urlType}/crear_cuenta?tax_system_id=${cotizadorConfig.id}&product_id=${cotizadorConfig.product_id}&person_type=${cotizadorConfig.code}&loan_amount=${selectedAmount}&loan_term=${selectedTerm}&tax_system_type=${cotizadorConfig.tax_system_type}`;

    if (creditType === "arrendamiento") {
      urlPortal += `&advance_percentage=${anticipo}&residual_percentage=${residual}`;
    }

    if (creditType === "credito_simple") {
      urlPortal += `&advance_percentage=${anticipo}`;
    }

    if (sourceCode) {
      urlPortal += `&source_code=${sourceCode}`;
    }
    navigate(urlPortal);
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sourceCode = params.get("code");

    if (sourceCode) {
      sessionStorage.setItem("source_code", sourceCode);
    } else {
      sessionStorage.removeItem("source_code");
    }
  }, []);

  const genarateQuotationTable = async () => {
    try {
      setIsLoading(true);

      const quotationResponse = await postGenerateQuotationTable({
        amount: selectedAmount,
        term: selectedTerm,
        company: interfaceConfig?.name || "",
        credit_type: "credito_simple",
      }).unwrap();

      const base64String = (() => {
        if (typeof quotationResponse === "string") return quotationResponse;
        if (!quotationResponse || typeof quotationResponse !== "object") return null;

        const data = (quotationResponse as { data?: unknown }).data;
        if (typeof data === "string") return data;
        if (data && typeof data === "object") {
          const nested = (data as { data?: unknown }).data;
          if (typeof nested === "string") return nested;
        }

        return null;
      })();

      if (!base64String) {
        throw new Error("No se pudo generar la tabla de cotización");
      }

      const byteCharacters = atob(base64String);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const file = new Blob([byteArray], { type: "application/pdf" });

      const fileURL = URL.createObjectURL(file);

      const link = document.createElement("a");
      link.href = fileURL;
      link.download = "tabla_amortizacion_credito_simple.pdf";

      document.body.appendChild(link);
      link.click();

      document.body.removeChild(link);
      URL.revokeObjectURL(fileURL);
    } catch (error) {
      const api_error = error as ApiError;
      dispatch(setModalErrorVisible({ open: true, error: api_error }));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Grid container height="100%">
        <Grid item xs={12} md={8}>
          <Container maxWidth={"sm"}>
            <Typography fontWeight={500} variant="h3" mt={4} color={theme?.palette?.primary?.dark} textAlign={"center"}>
              Cotizador
            </Typography>

            <Box>
              <Box>
                <Typography
                  fontWeight={500}
                  fontSize={"16px"}
                  mt={1}
                  color={theme?.palette?.primary?.dark}
                  textAlign={"center"}
                >
                  Selecciona tu régimen fiscal
                </Typography>

                <Grid container spacing={isMobileOrTablet ? 1 : 2} mt={1} mr={isMobileOrTablet ? 1 : 0}>
                  <Grid item xs={6} md={6} display="flex" justifyContent={"flex-end"}>
                    {!isLoading ? (
                      <ButtonTypeSelectedCustom
                        title="Crédito"
                        onClick={() => setCreditType("credito_simple")}
                        isSelected={creditType === "credito_simple"}
                        isMovile={isMobileOrTablet}
                        height={"60px"}
                        heightMoviles="60px"
                        width={"170px"}
                        widthMoviles="130px"
                        icon={icon_check}
                        width_icon="22px"
                        height_icon="22px"
                        font_size={"12px"}
                        top_icon="-12px"
                        font_size_movil="11px"
                      />
                    ) : (
                      <Skeleton
                        variant="rectangular"
                        animation="wave"
                        width={isMobileOrTablet ? 150 : 190}
                        height={isMobileOrTablet ? 60 : 70}
                      />
                    )}
                  </Grid>
                  <Grid item xs={6} md={6} display="flex" justifyContent={"flex-start"}>
                    {!isLoading ? (
                      <ButtonTypeSelectedCustom
                        title="Arrendamiento"
                        onClick={() => setCreditType("arrendamiento")}
                        isSelected={creditType === "arrendamiento"}
                        isMovile={isMobileOrTablet}
                        height={"60px"}
                        heightMoviles="60px"
                        width={"170px"}
                        widthMoviles="130px"
                        icon={icon_check}
                        width_icon="22px"
                        height_icon="22px"
                        font_size={"12px"}
                        top_icon="-12px"
                        font_size_movil="11px"
                      />
                    ) : (
                      <Skeleton
                        variant="rectangular"
                        animation="wave"
                        width={isMobileOrTablet ? 150 : 190}
                        height={isMobileOrTablet ? 60 : 70}
                      />
                    )}
                  </Grid>
                </Grid>
              </Box>

              <Box mt={4} ml={isMobileOrTablet ? 3 : 1} mr={isMobileOrTablet ? 3 : 1}>
                <Typography
                  fontWeight={500}
                  fontSize={"16px"}
                  color={theme?.palette?.primary?.dark}
                  textAlign={"center"}
                >
                  Selecciona el monto
                </Typography>

                <Typography
                  fontWeight={700}
                  fontSize={"13px"}
                  mt={1}
                  color={theme?.palette?.primary?.main}
                  textAlign={"center"}
                >
                  {amountToText(selectedAmount)}
                </Typography>

                {!isLoading ? (
                  <StyledSlider
                    aria-label="amount"
                    min={cotizadorConfig.minAmount}
                    max={cotizadorConfig.maxAmount}
                    step={cotizadorConfig.stepAmount}
                    marks={amountMarks}
                    defaultValue={cotizadorConfig.defaultAmount}
                    value={selectedAmount}
                    onChange={(_event, newValue) => setSelectedAmount(newValue as number)}
                  />
                ) : (
                  <Skeleton variant="rectangular" animation="wave" />
                )}
              </Box>

              <Box mb={1} ml={isMobileOrTablet ? 3 : 1} mr={isMobileOrTablet ? 3 : 1}>
                <Typography
                  fontWeight={500}
                  fontSize={"16px"}
                  mb={2}
                  color={theme?.palette?.primary?.dark}
                  textAlign={"center"}
                >
                  Selecciona el plazo en meses
                </Typography>

                {!isLoading ? (
                  <StyledSlider
                    aria-label="term"
                    min={cotizadorConfig.minTerm}
                    max={cotizadorConfig.maxTerm}
                    step={1}
                    defaultValue={cotizadorConfig.defaultTerm}
                    marks={termMarks}
                    value={selectedTerm}
                    onChange={(_event, newValue) => setSelectedTerm(newValue as number)}
                    valueLabelDisplay="on"
                  />
                ) : (
                  <Skeleton variant="rectangular" animation="wave" />
                )}
              </Box>

              <Box mb={1} ml={isMobileOrTablet ? 3 : 1} mr={isMobileOrTablet ? 3 : 1}>
                <Typography
                  fontWeight={500}
                  fontSize={"16px"}
                  mb={2}
                  color={theme?.palette?.primary?.dark}
                  textAlign={"center"}
                >
                  Anticipo
                </Typography>

                {!isLoading ? (
                  <StyledSlider
                    aria-label="advance"
                    min={cotizadorConfig.minAdvance}
                    max={cotizadorConfig.maxAdvance}
                    step={cotizadorConfig.stepAdvance}
                    defaultValue={cotizadorConfig.defaultAdvance}
                    marks={advancePercentageMarket}
                    value={anticipo}
                    onChange={(_event, newValue) => setAnticipo(newValue as number)}
                    valueLabelDisplay="on"
                  />
                ) : (
                  <Skeleton variant="rectangular" animation="wave" />
                )}
              </Box>

              {creditType === "arrendamiento" && (
                <Box mb={1} ml={isMobileOrTablet ? 3 : 1} mr={isMobileOrTablet ? 3 : 1}>
                  <Typography
                    fontWeight={500}
                    fontSize={"16px"}
                    mb={2}
                    color={theme?.palette?.primary?.dark}
                    textAlign={"center"}
                  >
                    Residual
                  </Typography>

                  <StyledSlider
                    aria-label="residual"
                    min={cotizadorConfig.minResidual}
                    max={cotizadorConfig.maxResidual}
                    step={cotizadorConfig.stepResidual}
                    defaultValue={cotizadorConfig.defaultResidual}
                    marks={residualPercentageMarket}
                    value={residual}
                    onChange={(_event, newValue) => setResidual(newValue as number)}
                    valueLabelDisplay="on"
                  />
                </Box>
              )}

              <Typography
                fontWeight={700}
                fontSize={"16px"}
                color={theme?.palette?.primary?.main}
                textAlign={"center"}
                m={4}
              >
                Pago mensual estimado*
              </Typography>

              <Typography
                mb={2}
                textAlign={"center"}
                variant="h4"
                fontWeight={theme?.palette?.fontWeights?.semibold}
                color={theme?.palette?.primary?.dark}
              >
                {isNaN(monthlyRate) || !isFinite(monthlyRate) ? "0" : formatCurrency(monthlyRate)}{" "}
                <Typography
                  variant="h5"
                  component={"span"}
                  fontWeight={theme?.palette?.fontWeights?.semibold}
                  color={theme?.palette?.primary?.dark}
                >
                  MXN
                </Typography>
              </Typography>

              <Box
                display="flex"
                justifyContent="center"
                gap={1}
                sx={{
                  cursor: isLoading ? "not-allowed" : "pointer",
                  textDecoration: isLoading ? "none" : "underline",
                  opacity: isLoading ? 0.5 : 1,
                  pointerEvents: isLoading ? "none" : "auto",
                }}
                onClick={() => {
                  if (!isLoading) {
                    return;
                    genarateQuotationTable();
                  }
                }}
              >
                <SystemUpdateAltIcon sx={{ color: theme?.palette?.primary?.dark }} />
                <Typography fontSize={"14px"} textAlign={"center"} color={theme?.palette?.primary?.dark}>
                  Descarga aquí la tabla de amortización informativa
                </Typography>
              </Box>

              <Typography
                textAlign={"center"}
                fontWeight={theme?.palette?.fontWeights?.light}
                color={theme?.palette?.primary?.dark}
                mt={2}
                mb={2}
              >
                <small>
                  *Esta simulación es para fines demostrativos y está sujeto a aprobación, las condiciones como la tasa,
                  el monto del crédito, o la comisión por apertura, podrían cambiar dependiendo el perfil y la situación
                  actual del solicitante.
                </small>
              </Typography>

              <Box display="flex" justifyContent="center" mb={2}>
                <Button variant="contained" onClick={setConditions} disabled={isLoading}>
                  Continuar
                </Button>
              </Box>
            </Box>
          </Container>
        </Grid>
        {!isMobileOrTablet && (
          <Grid item xs={12} md={4}>
            <CustomRightImage />
          </Grid>
        )}
      </Grid>
    </>
  );
};
export default CotizadorVivemoto;
